import { graphql } from '@data-access/graphql'
import type { ResultOf } from '@graphql-typed-document-node/core'

const _CartVendorFragmentDoc = graphql(`
  fragment CartVendor on MerchantObject {
    associationRegistrationNumber
    city
    companyRegistrationNumber
    createdAt
    profileText
    tradeName
    legalForm
    legalName
    country
    email
    phone
    street
    vatId
    zipCode
    image {
      url
      alt
    }
  }
`)

const _NexperienceVariantOptionFragmentDoc = graphql(`
  fragment NexperienceVariantOption on NexperienceVariantOption {
    id
    name
    description
    price
    currency
    min
    max
    groupSize
    duration
  }
`)

const NexperienceVariantFragmentDoc = graphql(`
  fragment NexperienceVariant on NexperienceVariant {
    id
    title
    maxCapacity
    options {
      ...NexperienceVariantOption
    }
  }
`)

const _CartExperienceFragmentDoc = graphql(`
  fragment CartExperience on Nexperience {
    id
    title
    cancelationDeadline
    experienceType
    alwaysAvailable
    offerPeriodStart {
      local
      timezone
      utc
    }
    offerPeriodEnd {
      local
      timezone
      utc
    }
    activities
    publicId
    phoneNumberNeeded
    hasMultiplePrices
    userTimeslotRequestsAllowed
    priceCategory
    currency
    slug
    destination {
      name
      slug
    }
    images {
      thumbnailUrl
      url
      alt
      copyright
    }
    location {
      name
      description
      address
      varying
      lat
      lng
      city
    }
    merchant {
      ...CartVendor
    }
    variants {
      ...NexperienceVariant
    }
  }
`)

const CartItemExperienceFragmentDoc = graphql(`
  fragment CartItemExperience on CartItemExperience {
    id
    currency
    status
    bookable
    total
    timeslotId
    visitDate
    variantId
    experienceId
    experience {
      ...CartExperience
    }
    timeslot {
      id
      startDate {
        local
        timezone
      }
      endDate {
        local
        timezone
      }
    }
    tickets {
      ticketId
      count
      price
      total
      name
      groupSize
      description
    }
    promotionCodeId
    promotionCodeName
    promotionCodeDiscount
    totalAfterDiscount
    promotionCodeType
    promotionCodeValue
  }
`)

const _CartBeachChairFragmentDoc = graphql(`
  fragment CartBeachChair on BeachChair {
    id
    publicNumber
    model
    lockId
    images
    rowName
    sectionName
  }
`)

const CartItemBeachChairFragmentDoc = graphql(`
  fragment CartItemBeachChair on CartItemBeachChair {
    id
    currency
    bookable
    statusBeachChair: status
    total
    startDate
    endDate
    cancelationDate {
      utc
      local
      timezone
    }
    beachSectionId
    beachChair {
      ...CartBeachChair
    }
    beachLocation {
      city
      coordinate {
        coordinates
      }
    }
    promotionCodeId
    promotionCodeName
    promotionCodeDiscount
    totalAfterDiscount
    promotionCodeType
    promotionCodeValue
  }
`)

const CartItemAccommodationFragmentDoc = graphql(`
  fragment CartItemAccommodation on CartItemAccommodation {
    id
    currency
    adultCount
    childrenAges
    checkin
    checkout
    cancelationDeadline {
      local
      timezone
    }
    refundSavedExpenses
    roomTitle
    rateTitle
    image {
      url
    }
    mealPlan
    touristTax {
      type
      amount
    }
    accommodation {
      slug
      id
      name
      occupancy {
        maxAdults
        maxChildren
        maxOccupancy
      }
      minLengthOfStay
      maxLengthOfStay
      servicePhone
      zipCode
      importantNotes {
        key
      }
      merchant {
        ...CartVendor
      }
      stars
      isRecommended
      lat
      lng
      reviewScore
      numberOfReviews
    }
    pricing {
      discountPercentage
      price
      currency
      originalPrice
      tags
    }
    total
    bookable
    allowSpecialRequests
    statusAccommodation: status
    promotionCodeId
    promotionCodeName
    promotionCodeDiscount
    totalAfterDiscount
    promotionCodeType
    promotionCodeValue
  }
`)

const CartItemBikeFragmentDoc = graphql(`
  fragment CartItemBike on CartItemRentableBike {
    id
    name
    available
    price
    quantity
  }
`)

const _CartItemRentableExtraFragmentDoc = graphql(`
  fragment CartItemRentableExtra on CartItemRentableExtra {
    available
    description
    id
    name
    price
    quantity
  }
`)

const CartItemRentableFragmentDoc = graphql(`
  fragment CartItemRentable on CartItemRentable {
    id
    currency
    bookable
    statusRentable: status
    total
    pricing {
      price
      originalPrice
    }
    merchant {
      city
      country
      legalName
      street
      tradeName
      zipCode
    }
    cancelationDeadlineRentable: cancelationDeadline {
      local
      timezone
      utc
    }
    endDate
    extras {
      ...CartItemRentableExtra
    }
    products {
      ...CartItemBike
    }
    station {
      address
      city
    }
    shopId
    shopName
    startDate
    stationId
    promotionCodeId
    promotionCodeName
    promotionCodeDiscount
    totalAfterDiscount
    promotionCodeType
    promotionCodeValue
  }
`)

const FreeGiftFragmentDoc = graphql(`
  fragment FreeGift on FreeGiftObject {
    type
    value
  }
`)

export const CartDocument = graphql(`
  query cart {
    cart {
      items {
        __typename
        ... on CartItemExperience {
          ...CartItemExperience
        }
        ... on CartItemBeachChair {
          ...CartItemBeachChair
        }
        ... on CartItemAccommodation {
          ...CartItemAccommodation
        }
        ... on CartItemRentable {
          ...CartItemRentable
        }
      }
      total
      currency
      bookable
      freeGift {
        ...FreeGift
      }
      promotionCodeId
      promotionCodeName
      promotionCodeDescription
      promotionCodeType
      promotionCodeValue
      discount
    }
  }
`)

export const CartCountDocument = graphql(`
  query cartCount {
    cartCount
  }
`)

const BeachChairRegionFragmentDoc = graphql(`
  fragment BeachChairRegion on CartRecommendationBeachChairRegion {
    name
    slug
    image {
      alt
      url
    }
  }
`)

const CartRecommendationExperienceFragmentDoc = graphql(`
  fragment CartRecommendationExperience on NexperienceSearchItem {
    __typename
    id
    publicId
    duration
    hasMultipleDurations
    hasMultiplePrices
    images {
      url
      alt
    }
    location
    price
    priceCategory
    currency
    slug
    title
    hasTimeslots
    lat
    lng
  }
`)

const CartRecommendationAccommodationFragmentDoc = graphql(`
  fragment CartRecommendationAccommodation on AccommodationSearchItem {
    __typename
    id
    slug
    images {
      url
    }
    name
    city
    isRecommended
    reviewScore
    numberOfReviews
    lat
    lng
  }
`)

export const CartRecommendationsDocument = graphql(`
  query cartRecommendations {
    cartRecommendations {
      experiences {
        ...CartRecommendationExperience
      }
      accommodations {
        ...CartRecommendationAccommodation
      }
      beachChairRegion {
        ...BeachChairRegion
      }
      from
      to
      total
    }
  }
`)

const GetRecommendationFragmentDoc = graphql(`
  fragment GetRecommendation on Recommendation {
    destination {
      slug
      name
      accommodationCount
      beachChairStatus
    }
    nexperiences {
      publicId
      slug
      images {
        url
      }
      price
      priceCategory
      currency
      hasMultiplePrices
      title
    }
    lat
    lng
    location
  }
`)

export const GetRecommendationsDocument = graphql(`
  query getRecommendations($input: GetRecommendationsInput!) {
    getRecommendations(input: $input) {
      ...GetRecommendation
    }
  }
`)

export const CartItemAccommodationsCreateDocument = graphql(`
  mutation cartItemAccommodationsCreate($input: CartItemAccommodationsInput!) {
    cartItemAccommodationsCreate(input: $input)
  }
`)

export const CartItemAccommodationUpsertDocument = graphql(`
  mutation cartItemAccommodationUpsert($input: CartItemAccommodationUpsertInput!, $itemId: String) {
    cartItemAccommodationUpsert(input: $input, itemId: $itemId)
  }
`)

export const CartItemBeachChairInsertDocument = graphql(`
  mutation cartItemBeachChairInsert($input: CartItemBeachChairInsertInput!) {
    cartItemBeachChairInsert(input: $input)
  }
`)

export const CartItemDeleteDocument = graphql(`
  mutation cartItemDelete($id: String!) {
    cartItemDelete(id: $id)
  }
`)

export const CartItemExperienceUpsertDocument = graphql(`
  mutation cartItemExperienceUpsert($input: CartItemExperienceUpsertInput!, $itemId: String) {
    cartItemExperienceUpsert(input: $input, itemId: $itemId)
  }
`)

export const CartItemRentableUpsertDocument = graphql(`
  mutation cartItemRentableUpsert($input: CartItemRentableUpsertInput!, $itemId: String) {
    cartItemRentableUpsert(input: $input, itemId: $itemId)
  }
`)

export type CartQuery = ResultOf<typeof CartDocument>
export type CartRecommendationsQuery = ResultOf<typeof CartRecommendationsDocument>
export type GetRecommendationsQuery = ResultOf<typeof GetRecommendationsDocument>

export type NexperienceVariantFragment = ResultOf<typeof NexperienceVariantFragmentDoc>
export type CartItemExperienceFragment = ResultOf<typeof CartItemExperienceFragmentDoc>
export type CartItemBeachChairFragment = ResultOf<typeof CartItemBeachChairFragmentDoc>
export type CartItemAccommodationFragment = ResultOf<typeof CartItemAccommodationFragmentDoc>
export type CartItemBikeFragment = ResultOf<typeof CartItemBikeFragmentDoc>
export type CartItemRentableFragment = ResultOf<typeof CartItemRentableFragmentDoc>
export type FreeGiftFragment = ResultOf<typeof FreeGiftFragmentDoc>
export type BeachChairRegionFragment = ResultOf<typeof BeachChairRegionFragmentDoc>
export type CartRecommendationExperienceFragment = ResultOf<
  typeof CartRecommendationExperienceFragmentDoc
>
export type CartRecommendationAccommodationFragment = ResultOf<
  typeof CartRecommendationAccommodationFragmentDoc
>
export type GetRecommendationFragment = ResultOf<typeof GetRecommendationFragmentDoc>
